// src/features/properties/PropertiesApiSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit"
import { apiSlice } from "../../app/api/apiSlice"

const propertiesAdapter = createEntityAdapter({
    sortComparer: false
})

const initialState = propertiesAdapter.getInitialState()

// 1. Đầu tiên inject endpoints
export const propertiesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getProperties: builder.query({
            query: ({ page = 1, limit = 20, quickSearch = '', phoneSearch = '', district = '', propertyType = '', newWard = '', position = '', numbers = [], dateType = 'updatedAt' }) => {
                const params = new URLSearchParams()
                params.append('page', page)
                params.append('limit', limit)
                if (quickSearch.trim()) params.append('search', quickSearch.trim())
                if (phoneSearch.trim()) params.append('phoneSearch', phoneSearch.trim())

                    if (Array.isArray(district)) {
                    district.forEach(d => params.append('district', d))
                }

                if (Array.isArray(propertyType)) {
                    propertyType.forEach(t => params.append('propertyType', t))
                }

                if (Array.isArray(newWard)) {
                    newWard.forEach(t => params.append('newWard', t))
                }

                if (Array.isArray(position)) {
                    position.forEach(t => params.append('position', t))
                }

                if (dateType) params.append('dateType', dateType)

                numbers.forEach(field => {
                    const { key, operator = '', value1 = '', value2 = '' } = field

                    // Bỏ qua hoàn toàn nếu không có operator VÀ không có value nào
                    if (!operator && !value1 && !value2) return

                    // 1. Trường hợp "Trong khoảng"
                    if (operator === 'range') {
                        if (value1) params.append(`${key}Min`, value1)
                        if (value2) params.append(`${key}Max`, value2)
                        return
                    }
                    // 2. Trường hợp có operator (<, >, ≤, ≥, eq) và có value1
                    if (operator && value1) {
                            params.append(`${key}[${operator}]`, value1) // gửi: length[lt]=9
                        return
                    }

                    // 3. Trường hợp đặc biệt: chỉ nhập value1 mà không chọn operator → coi như "Bằng"
                    if (!operator && value1) {
                        params.append(key, value1)
                        return
                    }
                })
                return `/properties?${params.toString()}`
            },
            transformResponse: (responseData) => {
                const properties = responseData.properties || []
                const loadedProperties = properties.map(prop => {
                    prop.id = prop._id
                    return prop
                })

                const normalized = propertiesAdapter.setAll(initialState, loadedProperties)

                return {
                    ...normalized,
                    pagination: responseData.pagination || {
                        current: 1,
                        pages: 1,
                        total: 0,
                        hasPrev: false,
                        hasNext: false
                    }
                }
            },
            providesTags: (result, error, arg) => [
                { type: 'Property', id: 'LIST' },
                ...(result?.ids || []).map(id => ({ type: 'Property', id }))
            ]
        }),

        addNewProperty: builder.mutation({
            query: (newProperty) => ({
                url: '/properties',
                method: 'POST',
                body: newProperty
            }),
            invalidatesTags: [{ type: 'Property', id: 'LIST' }]
        }),

        updateProperty: builder.mutation({
            query: ({ id, imagesToDelete = [], ...patch }) => ({
                url: '/properties',
                method: 'PATCH',
                body: { id, imagesToDelete, ...patch }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Property', id: arg.id },
                { type: 'Property', id: 'LIST' }
            ]
        }),

        deleteProperty: builder.mutation({
            query: ({ id }) => ({
                url: '/properties',
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Property', id: arg.id },
                { type: 'Property', id: 'LIST' }
            ]
        }),

        tempDeleteProperty: builder.mutation({
            query: ({ id, restore = false }) => ({
                url: '/properties/tempDelete',
                method: 'PATCH',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Property', id: arg.id },
                { type: 'Property', id: 'LIST' }
            ]
        }),

        checkPhoneExists: builder.query({
            query: (params) => {
                const { phone, excludeId } = typeof params === 'string' ? { phone: params } : params
                const searchParams = new URLSearchParams()
                searchParams.append('phone', phone)
                if (excludeId) searchParams.append('excludeId', excludeId)
                return `/properties/phoneExists?${searchParams.toString()}`
            },
            transformResponse: (response) => response.exists === true,
        }),

        getPropertyActivityLogs: builder.query({
            query: ({ propertyId, page = 1 }) => `/properties/${propertyId}/activity?page=${page}`,
            providesTags: (result, error, { propertyId }) => [
                { type: 'ActivityLog', id: `PROP-${propertyId}-LIST` }
            ]
        }),
    })
})

// 2. Export hooks
export const {
    useGetPropertiesQuery,
    useAddNewPropertyMutation,
    useUpdatePropertyMutation,
    useDeletePropertyMutation,
    useTempDeletePropertyMutation,
    useCheckPhoneExistsQuery,
    useGetPropertyActivityLogsQuery,
} = propertiesApiSlice

// 3. Selector
export const selectAllProperties = (state) => {
    const result = propertiesApiSlice.endpoints.getProperties.select()(state)
    return result?.data ? propertiesAdapter.getSelectors(s => result.data ?? initialState).selectAll(result.data) : []
}

export const selectPropertiesPagination = (state) => {
    const result = propertiesApiSlice.endpoints.getProperties.select()(state)
    return result?.data?.pagination || { current: 1, pages: 1, total: 0, hasPrev: false, hasNext: false }
}